/**
 * fipe/mappers.js
 *
 * Funções de mapeamento que normalizam as respostas de cada fonte
 * para o formato interno uniforme: Array<{ Value: string|number, Label: string }>
 *
 * API oficial FIPE (veiculos.fipe.org.br) → já retorna { Value, Label }
 * Parallelum v2 (fipe.parallelum.com.br)  → retorna { code, name }
 */

// ---------------------------------------------------------------------------
// Marcas
// ---------------------------------------------------------------------------

/**
 * API oficial FIPE → já retorna [{ Value, Label }]
 */
export function mapBrandsFromFipe(data) {
  if (!Array.isArray(data)) return [];
  return data;
}

/**
 * Parallelum v2 → GET /{vehicleType}/brands → [{ code, name }]
 * Converte para [{ Value, Label }]
 */
export function mapBrandsFromParallelum(data) {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    Value: item.code,
    Label: item.name,
  }));
}

// ---------------------------------------------------------------------------
// Modelos
// ---------------------------------------------------------------------------

/**
 * API oficial FIPE → retorna { Modelos: [{ Value, Label }], Anos: [...] }
 * Extraímos somente os Modelos.
 */
export function mapModelsFromFipe(data) {
  // A API retorna um objeto com "Modelos" e "Anos"
  const models = data?.Modelos ?? (Array.isArray(data) ? data : []);
  return models;
}

/**
 * Parallelum v2 → GET /{vehicleType}/brands/{id}/models → { models: [{ code, name }] }
 * Converte para [{ Value, Label }]
 */
export function mapModelsFromParallelum(data) {
  const models = data?.models ?? (Array.isArray(data) ? data : []);
  return models.map((item) => ({
    Value: item.code,
    Label: item.name,
  }));
}

// ---------------------------------------------------------------------------
// Anos / Versões
// ---------------------------------------------------------------------------

/**
 * API oficial FIPE → já retorna [{ Value, Label }]
 */
export function mapYearsFromFipe(data) {
  if (!Array.isArray(data)) return [];
  return data;
}

/**
 * Parallelum v2 → GET /{vehicleType}/brands/{id}/models/{id}/years → [{ code, name }]
 * Converte para [{ Value, Label }]
 */
export function mapYearsFromParallelum(data) {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    Value: item.code,
    Label: item.name,
  }));
}

// ---------------------------------------------------------------------------
// Resultado FIPE (preço / valor do veículo)
// ---------------------------------------------------------------------------

/**
 * API oficial FIPE → ConsultarValorComTodosParametros
 * Retorna:
 * {
 *   Valor: "R$ 45.000,00",
 *   Marca: "VW - VolksWagen",
 *   Modelo: "AMAROK ...",
 *   AnoModelo: 2014,
 *   Combustivel: "Diesel",
 *   CodigoFipe: "005340-6",
 *   SiglaCombustivel: "D",
 *   ...
 * }
 *
 * Converte para o formato interno usado pelos componentes:
 * { brand, model, fipe_code, value, year, id_fuel, fuel }
 */
export function mapFipeResultFromFipe(data) {
  if (!data) return null;

  const fuelMap = { G: 1, A: 2, D: 3 };
  const id_fuel = fuelMap[data.SiglaCombustivel] ?? 1;

  const value = parseBRLValue(data.Valor);

  return {
    brand: data.Marca,
    model: data.Modelo,
    fipe_code: data.CodigoFipe,
    value,
    year: data.AnoModelo,
    id_fuel,
    fuel: data.Combustivel,
  };
}

/**
 * Parallelum v2 → GET /{vehicleType}/brands/{id}/models/{id}/years/{yearCode}
 * Retorna:
 * {
 *   price: "R$ 42.483,00",
 *   brand: "VW - VolksWagen",
 *   model: "AMAROK ...",
 *   modelYear: 2014,
 *   fuel: "Diesel",
 *   codeFipe: "005340-6",
 *   fuelAcronym: "D",
 *   ...
 * }
 *
 * Converte para o mesmo formato interno.
 */
export function mapFipeResultFromParallelum(data) {
  if (!data) return null;

  const fuelMap = { G: 1, A: 2, D: 3 };
  const id_fuel = fuelMap[data.fuelAcronym] ?? 1;

  const value = parseBRLValue(data.price);

  return {
    brand: data.brand,
    model: data.model,
    fipe_code: data.codeFipe,
    value,
    year: data.modelYear,
    id_fuel,
    fuel: data.fuel,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converte string monetária BRL "R$ 42.483,00" → 42483.00 (number)
 */
function parseBRLValue(str) {
  if (!str) return 0;
  return parseFloat(
    str.replace(/R\$\s*/g, '').replace(/\./g, '').replace(',', '.'),
  ) || 0;
}
